import Button from '@material-ui/core/Button'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import React, { useCallback, useState } from 'react'
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone'
import { useFormUploadInput } from '../../../context/FormUploadContext'
import { toastError } from '../../../customs/Toast/Toast'
import { humanFileSize } from '../../../utils/humanFileSize'
import { useStylesUpload } from './stylesUpload'

interface iUploadInput {
  name: string
  disabled?: boolean
  required?: boolean
  enableFolder?: boolean,
  acceptFileExts?: string[]
  minSize?: number
  maxSize?: number
  maxFiles?: number
  maxSizeAll?: number
}

const UploadInput = ({
  name,
  disabled,
  required,
  enableFolder,
  acceptFileExts,
  minSize,
  maxSize,
  maxFiles,
  maxSizeAll,
}: iUploadInput) => {
  const classes = useStylesUpload()

  const { uploadFile, filesUploaded, filesUploading } = useFormUploadInput(name)

  const onDropAccept = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        uploadFile(file)
      })
    },
    [uploadFile],
  )

  const onDropReject = useCallback((rejectedFiles: FileRejection[]) => {
    rejectedFiles.forEach((file) => {
      // console.log('ERROR file upload', file.errors)
      const err = file.errors[0].code
      switch (err) {
        case ErrorCode.FileInvalidType:
          toastError('Nepovolený typ souboru: ' + file.file.name)
          break
        case ErrorCode.FileTooLarge:
          toastError('Velikost souboru je větší než povolený limit: ' + file.file.name)
          break
        case ErrorCode.FileTooSmall:
          toastError('Velikost souboru je menší než povolený limit: ' + file.file.name)
          break
        case ErrorCode.TooManyFiles:
          toastError('Nepovolený počet souborů: ' + file.file.name)
          break
        default:
          toastError('Chyba souboru: ' + file.file.name)
      }
    })
  }, [])

  const onlyOne = !!maxFiles && maxFiles < 2
  const numUploadFiles = filesUploaded.length + filesUploading.length
  const numResFiles = !!maxFiles ? maxFiles - numUploadFiles : undefined
  const isFull = numResFiles !== undefined && numResFiles < 1
  const filesFormatInfo = acceptFileExts?.join(', ').toUpperCase()

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: onDropAccept,
    onDropRejected: onDropReject,
    disabled: disabled,
    maxSize: maxSize,
    minSize: minSize,
    // validator: nameLengthValidator(file)
    multiple: !onlyOne,
    maxFiles: numResFiles,

    // nekontroluje se mime type ale jen pripony - https://github.com/react-dropzone/react-dropzone/issues/1220#issuecomment-1200855274
    useFsAccessApi: false,
    accept: acceptFileExts ? {
      '': acceptFileExts.map(ext => `.${ext}`) ?? []
    } : undefined,
    // noClick: true,
  })

  const [isFolder, setIsFolder] = useState(false)
  const inputDirAttr = enableFolder && isFolder ? { webkitdirectory: '', directory: '' } : {}

  if (disabled || isFull) {
    return null
  }

  return (
    <div className={classes.uploadInputWrap} {...getRootProps()}>
      <input
        id={'file-' + name}
        {...getInputProps()}
        aria-required={required ? 'true' : 'false'}
        aria-disabled={disabled ? 'true' : 'false'}
        {...inputDirAttr}
        style={{ display: 'block', width: 0, height: 0 }}
      />
      <div>
        <p>
          {isDragActive ? 'Přetáhněte soubory sem ...' : 'Přetáhněte soubor nebo'}
        </p>
        {/*<label*/}
        {/*  htmlFor={'file-' + name}*/}
        {/*>*/}
        <Button
          variant="contained"
          color="default"
          startIcon={<CloudUploadIcon/>}
        >
          Nahrajte ze zařízení
        </Button>
        {/*</label>*/}
        {filesFormatInfo && <p className={classes.uploadNote}>podporované formáty {filesFormatInfo}</p>}
        {maxFiles && <p className={classes.uploadNote}>maximální počet příloh je {maxFiles} souborů</p>}
        {maxSize &&
          <p className={classes.uploadNote}>maximální velikost jednoho souboru je {maxSize.toLocaleString()} bajtů ({humanFileSize(maxSize)})</p>}
        {maxSizeAll &&
          <p className={classes.uploadNote}>maximální velikost všech příloh je {maxSizeAll.toLocaleString()} bajtů ({humanFileSize(maxSizeAll)})</p>}

        {enableFolder && !onlyOne && (
          <p
            className={classes.uploadNote}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              checked={isFolder}
              onChange={(e) => setIsFolder(e.target.checked)}
              type="checkbox"
              id={'isFolder-' + name}
            />{' '}
            <label className="ms-2" htmlFor={'isFolder- ' + name}>
              nahrát celou složku
            </label>
          </p>
        )}

      </div>
    </div>
  )
}

export default UploadInput
